import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import JobsDock from "./components/JobsDock";
import DryRunBanner from "./components/DryRunBanner";
import ErrorBoundary from "./components/ErrorBoundary";

// ── EAGER pages: shown immediately after login OR on first paint.
// Keeping these eager avoids a flicker on the most common landings.
import { LoginPage, RegisterPage } from "./pages/AuthPages";
import { DashboardPage } from "./pages/DashboardPage";
import { PrivacyPolicyPage, TermsOfUsePage, DataDeletionPage } from "./pages/LegalPages";

// ── LAZY pages: code-split into separate chunks. Only downloaded when the
// user actually navigates to that route. Massively reduces initial bundle
// and speeds up first paint + subsequent navigations (cached after 1st visit).
const ProductsPage          = lazy(() => import("./pages/ProductsPage").then(m => ({ default: m.ProductsPage })));
const OpportunitiesPage     = lazy(() => import("./pages/OpportunitiesPage").then(m => ({ default: m.OpportunitiesPage })));
const ListingsPage          = lazy(() => import("./pages/ListingsPage").then(m => ({ default: m.ListingsPage })));
const CalculatorPage        = lazy(() => import("./pages/CalculatorPage").then(m => ({ default: m.CalculatorPage })));
const SettingsPage          = lazy(() => import("./pages/SettingsPage").then(m => ({ default: m.SettingsPage })));
const CompetitorsPage       = lazy(() => import("./pages/CompetitorsPage").then(m => ({ default: m.CompetitorsPage })));
const AdsFulfillmentPage    = lazy(() => import("./pages/AdsFulfillmentPage").then(m => ({ default: m.AdsFulfillmentPage })));
const ComparePage           = lazy(() => import("./pages/ComparePage").then(m => ({ default: m.ComparePage })));
const DraftsPage            = lazy(() => import("./pages/DraftsPage").then(m => ({ default: m.DraftsPage })));
const AlertsPage            = lazy(() => import("./pages/AlertsPage").then(m => ({ default: m.AlertsPage })));
const CreativeLibraryPage   = lazy(() => import("./pages/CreativeLibraryPage").then(m => ({ default: m.CreativeLibraryPage })));
const IntegrationsPage      = lazy(() => import("./pages/IntegrationsPage").then(m => ({ default: m.IntegrationsPage })));
const SystemHealthPage      = lazy(() => import("./pages/SystemHealthPage").then(m => ({ default: m.SystemHealthPage })));
const MeliListingsPage      = lazy(() => import("./pages/MeliListingsPage").then(m => ({ default: m.MeliListingsPage })));
const MeliOrdersPage        = lazy(() => import("./pages/MeliOrdersPage").then(m => ({ default: m.MeliOrdersPage })));
const MeliDashboardPage     = lazy(() => import("./pages/MeliDashboardPage").then(m => ({ default: m.MeliDashboardPage })));
const MarketRadarIndexerPage    = lazy(() => import("./pages/MarketRadarIndexerPage").then(m => ({ default: m.MarketRadarIndexerPage })));
const MarketRadarAuditPage      = lazy(() => import("./pages/MarketRadarAuditPage").then(m => ({ default: m.MarketRadarAuditPage })));
const CollectorTargetsPage      = lazy(() => import("./pages/CollectorTargetsPage").then(m => ({ default: m.CollectorTargetsPage })));
const MarketRadarCategoriesPage = lazy(() => import("./pages/MarketRadarCategoriesPage").then(m => ({ default: m.MarketRadarCategoriesPage })));
const MarketRadarDetailPage     = lazy(() => import("./pages/MarketRadarDetailPage").then(m => ({ default: m.MarketRadarDetailPage })));
const MarketRadarCollectorPage  = lazy(() => import("./pages/MarketRadarCollectorPage").then(m => ({ default: m.MarketRadarCollectorPage })));

// New Core Pages (default exports)
const ListingCreatorPage      = lazy(() => import("./pages/ListingCreatorPage"));
const ProductAnalyzerPage     = lazy(() => import("./pages/ProductAnalyzerPage"));
const AIConfigsPage           = lazy(() => import("./pages/AIConfigsPage"));
const WhatsAppManagerPage     = lazy(() => import("./pages/WhatsAppManagerPage"));
const RentabilidadePage       = lazy(() => import("./pages/RentabilidadePage"));
const ListingCopierPage       = lazy(() => import("./pages/ListingCopierPage"));
const ApprovalsPage           = lazy(() => import("./pages/ApprovalsPage"));
const AdsIntelligencePage     = lazy(() => import("./pages/AdsIntelligencePage"));
const AutopilotMonitorPage    = lazy(() => import("./pages/AutopilotMonitorPage"));
const VideoGeneratorPage      = lazy(() => import("./pages/VideoGeneratorPage"));
const CollectorAgentPanelPage = lazy(() => import("./pages/CollectorAgentPanelPage"));
const MarketplaceCentralPage  = lazy(() => import("./pages/MarketplaceCentralPage"));
const ActivitiesPage          = lazy(() => import("./pages/ActivitiesPage").then(m => ({ default: m.ActivitiesPage })));

const FullPageSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#27272A]"></div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullPageSpinner />;
  if (!user) return <Navigate to="/login" state={{ from: `${location.pathname}${location.search}${location.hash}` }} replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullPageSpinner />;
  if (user) return <Navigate to={location.state?.from || "/dashboard"} replace />;
  return children;
};

const PR = ({ children }) => (
  <ProtectedRoute>
    <ErrorBoundary>{children}</ErrorBoundary>
  </ProtectedRoute>
);

function AppRoutes() {
  return (
    <Suspense fallback={<FullPageSpinner />}>
      <Routes>
        {/* Public */}
        <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-of-use" element={<TermsOfUsePage />} />
        <Route path="/data-deletion" element={<DataDeletionPage />} />

        {/* Core Modules */}
        <Route path="/dashboard" element={<PR><DashboardPage /></PR>} />
        <Route path="/activities" element={<PR><ActivitiesPage /></PR>} />
        <Route path="/listing-creator" element={<PR><ListingCreatorPage /></PR>} />
        <Route path="/fabrica-anuncios" element={<Navigate to="/listing-creator" replace />} />
        <Route path="/product-analyzer" element={<PR><ProductAnalyzerPage /></PR>} />
        <Route path="/rentabilidade" element={<PR><RentabilidadePage /></PR>} />
        <Route path="/listing-copier" element={<PR><ListingCopierPage /></PR>} />
        <Route path="/marketplace" element={<PR><MarketplaceCentralPage /></PR>} />
        <Route path="/approvals" element={<PR><ApprovalsPage /></PR>} />
        <Route path="/video-generator" element={<PR><VideoGeneratorPage /></PR>} />
        <Route path="/ads-intelligence" element={<PR><AdsIntelligencePage /></PR>} />
        <Route path="/autopilot" element={<PR><AutopilotMonitorPage /></PR>} />

        {/* Market Radar */}
        <Route path="/market-radar" element={<PR><MarketRadarCategoriesPage /></PR>} />
        <Route path="/market-radar/v2" element={<PR><MarketRadarCollectorPage /></PR>} />
        <Route path="/market-radar/category/:categoryId" element={<PR><MarketRadarDetailPage /></PR>} />
        <Route path="/market-radar/category/:categoryId/subcategory/:subCategoryId" element={<PR><MarketRadarDetailPage /></PR>} />
        <Route path="/market-radar/v1" element={<PR><MarketRadarIndexerPage /></PR>} />
        <Route path="/market-radar/audit" element={<PR><MarketRadarAuditPage /></PR>} />
        <Route path="/market-radar/targets" element={<PR><CollectorTargetsPage /></PR>} />
        <Route path="/intelligence/collector" element={<PR><CollectorAgentPanelPage /></PR>} />

        {/* Catalog */}
        <Route path="/products" element={<PR><ProductsPage /></PR>} />
        <Route path="/opportunities" element={<PR><OpportunitiesPage /></PR>} />
        <Route path="/competitors" element={<PR><CompetitorsPage /></PR>} />
        <Route path="/drafts" element={<PR><DraftsPage /></PR>} />
        <Route path="/listings" element={<PR><ListingsPage /></PR>} />

        {/* Tools */}
        <Route path="/calculator" element={<PR><CalculatorPage /></PR>} />
        <Route path="/compare" element={<PR><ComparePage /></PR>} />
        <Route path="/ads-fulfillment" element={<PR><AdsFulfillmentPage /></PR>} />
        <Route path="/creative-library" element={<PR><CreativeLibraryPage /></PR>} />
        <Route path="/alerts" element={<PR><AlertsPage /></PR>} />

        {/* System */}
        <Route path="/integrations" element={<PR><IntegrationsPage /></PR>} />
        <Route path="/integrations/openai" element={<PR><AIConfigsPage /></PR>} />
        <Route path="/integrations/whatsapp" element={<PR><WhatsAppManagerPage /></PR>} />
        <Route path="/settings/system-health" element={<PR><SystemHealthPage /></PR>} />
        <Route path="/ai-configs" element={<Navigate to="/integrations/openai" replace />} />
        <Route path="/whatsapp" element={<Navigate to="/integrations/whatsapp" replace />} />
        <Route path="/system/health" element={<Navigate to="/settings/system-health" replace />} />
        <Route path="/settings" element={<PR><SettingsPage /></PR>} />

        {/* MELI */}
        <Route path="/meli/listings" element={<PR><MeliListingsPage /></PR>} />
        <Route path="/meli/orders" element={<PR><MeliOrdersPage /></PR>} />
        <Route path="/meli/dashboard" element={<PR><MeliDashboardPage /></PR>} />

        {/* Default */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <DryRunBanner />
        <AppRoutes />
        <Toaster position="top-right" richColors />
        <JobsDock />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
